import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { UploadProgressBar } from "./UploadProgressBar";
import { useApp } from "../context/AppContext";
import { api, openWS, mediaSrcById } from "../api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { toast } from "sonner";
import {
  Home, Briefcase, MessageSquare, Bell, Menu, Globe, LogOut, WifiOff, Search as SearchIcon,
  RefreshCw, Trash2, Clock, ChevronRight, CloudOff, Building2,
} from "lucide-react";
import { subscribeQueue, flushQueue, retryQueued, removeQueued } from "../lib/offlineQueue";
import { updateAppBadge } from "../lib/badge";
import { vibrateForMessage } from "../lib/haptics";
import { WelcomeTour } from "./WelcomeTour";
import { PasskeyPrompt } from "./PasskeyPrompt";
import { FloatingIssueAlert } from "./FloatingIssueAlert";
import { CompanyInviteBanner } from "./CompanyInviteBanner";
import { BuddyAssistant } from "./BuddyAssistant";
import { buddyEnabled } from "../lib/buddyPrefs";
import { BroadcastViewer } from "./BroadcastViewer";
import { AIConsentDialog } from "./AIConsentDialog";
import { OfflineSetupDialog } from "./OfflineSetupDialog";
import { OfflineFullDialog } from "./OfflineFullDialog";
import { UpdateBanner, useAppOutdated } from "./UpdateBanner";
import { VoiceMiniPlayer } from "./VoiceMiniPlayer";
import { needsOfflineChoice, offlineEnabled, syncNow, subscribeOffline, hydrateMedia, state as offlineState } from "../lib/offlineSync";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

function initials(name) {
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export const Shell = ({ children }) => {
  const { user, setUser, logout, lang, setLang, t } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [queued, setQueued] = useState([]);
  const [queueOpen, setQueueOpen] = useState(false);
  const queuedCount = queued.length;

  // Company shown in the globe/user menu (small logo + name + my position)
  const [myCompany, setMyCompany] = useState(null);
  useEffect(() => {
    if (!user) { setMyCompany(null); return; }
    api.get("/company/mine").then((r) => setMyCompany(r.data.company)).catch(() => setMyCompany(null));
  }, [user?.id, user?.company_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mobile: hide the bottom tabs while the keyboard is open so it sits
  // right against the message composer (WhatsApp behavior).
  const [kbOpen, setKbOpen] = useState(false);
  const appOutdated = useAppOutdated();
  useEffect(() => {
    // Native app: trust the REAL keyboard (Capacitor Keyboard events via keyboardFix.js).
    // focus/blur alone is unreliable there: iOS does not fire focusout when a focused
    // input unmounts (e.g. leaving the list search), which left the tabs hidden with no keyboard.
    const onKb = (e) => setKbOpen(!!e.detail?.open);
    window.addEventListener("osc-keyboard", onKb);
    if (Capacitor.isNativePlatform()) return () => window.removeEventListener("osc-keyboard", onKb);
    const isEditable = (el) =>
      !!el && (el.tagName === "TEXTAREA" || (el.tagName === "INPUT" && !["checkbox", "radio", "file", "button", "range"].includes(el.type)) || el.isContentEditable);
    const onFocusIn = (e) => { if (isEditable(e.target)) setKbOpen(true); };
    const onFocusOut = () => {
      setTimeout(() => { if (!isEditable(document.activeElement)) setKbOpen(false); }, 80);
    };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      window.removeEventListener("osc-keyboard", onKb);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);
  // Any route change: the keyboard is never open on a fresh screen (also covers the unmounted-input case on web)
  useEffect(() => { setKbOpen(false); }, [location.pathname]);

  // ---- Offline mirror: first-run choice (native), sync on open / reconnect / resume / new message ----
  const [offlineAsk, setOfflineAsk] = useState(false);
  const [offlineFull, setOfflineFull] = useState(false);
  const [syncInfo, setSyncInfo] = useState({ ...offlineState });
  useEffect(() => {
    if (!user) return undefined;
    hydrateMedia();
    if (needsOfflineChoice(user.id)) setOfflineAsk(true);
    else if (offlineEnabled(user.id)) syncNow(user);
    const unsub = subscribeOffline((type, d) => {
      if (type === "state") setSyncInfo({ ...d });
      if (type === "full") setOfflineFull(true);
    });
    const onOnline = () => { if (offlineEnabled(user.id)) setTimeout(() => syncNow(user), 1500); };
    const onVisible = () => { if (!document.hidden && offlineEnabled(user.id)) syncNow(user); };
    const onNotify = (e) => { const inner = e.detail?.inner; if ((inner === "message" || inner === "conversation_new") && offlineEnabled(user.id)) syncNow(user); };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("osc-user-notify", onNotify);
    return () => { unsub(); window.removeEventListener("online", onOnline); document.removeEventListener("visibilitychange", onVisible); window.removeEventListener("osc-user-notify", onNotify); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Track connection status + offline queue size
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    const unsub = subscribeQueue((q) => setQueued(q));
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      unsub();
    };
  }, []);

  const refreshUnread = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get(`/conversations?user_id=${user.id}`);
      // Accumulated unread MESSAGES (WhatsApp-style): 5 messages → badge "5",
      // not the number of conversations with something unread
      const total = res.data.reduce((s, c) => s + (c.unread || 0), 0);
      setUnread(total);
      // App icon badge = TOTAL unread messages (matches the aps.badge sent in pushes)
      updateAppBadge(total);
      // Buddy (hidden) peeks more often while there is something unread
      try { window.__oscUnread = total; window.dispatchEvent(new CustomEvent("osc-unread-count", { detail: { total } })); } catch (e) { /* ignore */ }
    } catch (e) { /* ignore */ }
  }, [user]);

  useEffect(() => {
    refreshUnread();
  }, [refreshUnread, location.pathname]);

  // Returning to the foreground (native app resume / tab focus): re-sync unread + icon badge
  useEffect(() => {
    const onVisible = () => { if (!document.hidden) refreshUnread(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refreshUnread]);

  // Delivery ACK (✓✓ gray for senders): everything fetched by this device counts
  // as delivered — on app open and every time it returns to the foreground.
  useEffect(() => {
    if (!user) return undefined;
    const ack = () => api.post("/delivered-all", { user_id: user.id }).catch(() => {});
    ack();
    const onVisible = () => { if (!document.hidden) ack(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Push alerts: user-channel WebSocket + toast + browser Notification
  useEffect(() => {
    if (!user) return undefined;
    if ("Notification" in window && Notification.permission === "default") {
      try { Notification.requestPermission().catch(() => {}); } catch (e) { /* ignore */ }
    }
    const close = openWS(`user_${user.id}`, (payload) => {
      if (payload.inner === "typing") {
        // ephemeral — no refetch; Buddy listens to peek while someone writes to you
        try { window.dispatchEvent(new CustomEvent("osc-user-notify", { detail: payload })); } catch (e) { /* ignore */ }
        return;
      }
      refreshUnread();
      // Re-broadcast to interested global widgets (e.g. FloatingIssueAlert)
      try { window.dispatchEvent(new CustomEvent("osc-user-notify", { detail: payload })); } catch (e) { /* ignore */ }
      if (payload.inner === "message" && payload.conversation_id && payload.sender_id && payload.sender_id !== user.id) {
        // Delivery ACK: this device received the message live (regardless of screen)
        api.post(`/conversations/${payload.conversation_id}/delivered`, { user_id: user.id }).catch(() => {});
      }
      if (payload.inner === "message" && payload.preview && payload.sender_id && payload.sender_id !== user.id) {
        // User preference: notifications can be fully disabled in Settings
        if (localStorage.getItem("pmf_notifications") === "off") return;
        // Muted conversation (server already applied the "job today" exception):
        // message arrives silently — no vibration, no toast, no sound
        if (payload.muted) return;
        // Vibrate for EVERY incoming message while the app is open —
        // including when the user is looking at that exact conversation.
        vibrateForMessage();
        const p = payload.preview;
        const mentioned = Array.isArray(p.mentions) && p.mentions.includes(user.id);
        const onThatChat = window.location.pathname === `/chat/${payload.conversation_id}`;
        // Mentions always alert loudly — even while looking at that chat
        if (onThatChat && !document.hidden && !mentioned) return;
        const isHigh = p.priority === "high";
        const title = mentioned
          ? `\u{1F4E3} ${p.sender_name} ${t("mentioned_you")}`
          : `${isHigh ? "\u{1F6A8} " : ""}${p.conversation_type === "dm" ? p.sender_name : (p.conversation_name || p.sender_name)}`;
        const typeIcon = p.type === "voice" ? "\u{1F399} " : p.type === "photo" ? "\u{1F4F7} " : p.type === "video" ? "\u{1F3A5} " : p.type === "issue" ? "\u{26A0} " : "";
        const body = `${p.conversation_type !== "dm" ? `${p.sender_name}: ` : ""}${typeIcon}${p.content || ""}`;
        toast(title, {
          description: body,
          duration: mentioned ? 8000 : 4000,
          action: { label: "\u2192", onClick: () => navigate(`/chat/${payload.conversation_id}`) },
        });
        if ((document.hidden || mentioned) && "Notification" in window && Notification.permission === "granted") {
          try {
            const n = new Notification(title, { body, icon: "/onsitecomms-icon-128.png", tag: payload.conversation_id });
            n.onclick = () => { window.focus(); navigate(`/chat/${payload.conversation_id}`); };
          } catch (e) { /* ignore */ }
        }
      }
    });
    return close;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const navItems = [
    { key: "home", label: t("home"), icon: Home, path: "/home" },
    { key: "messages", label: t("messages"), icon: MessageSquare, path: "/messages", badge: unread },
    { key: "jobs", label: t("jobs"), icon: Briefcase, path: "/jobs" },
    { key: "search", label: t("global_search"), icon: SearchIcon, path: "/search" },
    { key: "activity", label: t("activity"), icon: Bell, path: "/activity" },
    { key: "more", label: t("more"), icon: Menu, path: "/more" },
  ];

  const isActive = (path) => location.pathname === path || (path !== "/home" && location.pathname.startsWith(path));

  return (
    // App frame: fixed viewport height, bars are static flex items and ONLY the
    // main area scrolls. This guarantees headers/footers never scroll away on
    // iOS/Android (position:fixed is unreliable in mobile Safari).
    <div className="app-shell-height bg-background flex overflow-hidden">
      {/* Desktop / iPad sidebar — top padding clears the iOS status bar (safe-area) */}
      <aside
        className="hidden md:flex md:flex-col w-64 border-r bg-card shrink-0"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="px-5 py-5 border-b flex items-center gap-2.5">
          <img src="/onsitecomms-icon.png" alt="OnSiteComms" className="h-10 w-10 rounded-lg object-contain" />
          <div>
            <div className="font-display font-semibold text-lg leading-none">
              <span className="text-[#2B3990]">OnSite</span><span className="text-[#C9A03B]">Comms</span>
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{t("app_tagline")}</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => (
            <button
              key={item.key}
              data-testid={`nav-${item.key}`}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive(item.path)
                  ? "bg-primary/10 text-primary border-l-2 border-primary"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              <item.icon className="h-5 w-5" strokeWidth={2} />
              {item.label}
              {item.badge > 0 && (
                <Badge className="ml-auto bg-primary text-primary-foreground" data-testid="nav-messages-unread">{item.badge > 99 ? "99+" : item.badge}</Badge>
              )}
            </button>
          ))}
        </nav>
        <div className="px-4 py-4 border-t">
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9">
              {user?.avatar_media_id && (
                <AvatarImage src={mediaSrcById(user.avatar_media_id)} alt="" className="object-cover" />
              )}
              <AvatarFallback style={{ backgroundColor: user?.avatar_color, color: "#fff" }} className="text-xs font-semibold">
                {user ? initials(user.name) : ""}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-muted-foreground">{t(user?.role)}</div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="user-menu-button" className="h-9 w-9">
                  <Globe className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                {/* My company (small logo + name) + my position */}
                <div className="px-2 py-2 flex items-center gap-2.5" data-testid="user-menu-company">
                  {myCompany?.logo_base64 ? (
                    <img
                      src={`data:image/jpeg;base64,${myCompany.logo_base64}`}
                      alt=""
                      className="h-8 w-8 rounded-md object-contain bg-white border shrink-0"
                      data-testid="user-menu-company-logo"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                      <Building2 className="h-4 w-4 text-primary" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="text-xs font-semibold truncate" data-testid="user-menu-company-name">
                      {myCompany?.name || t("no_company")}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate" data-testid="user-menu-position">
                      {user?.title || t(user?.role)}
                    </div>
                  </div>
                </div>
                <DropdownMenuSeparator />
                {/* Language picker — flags, tap to switch */}
                <div className="px-2 py-1.5 flex items-center justify-center gap-1.5" data-testid="language-toggle">
                  {[
                    { code: "en", flag: "🇺🇸", label: "English" },
                    { code: "pt", flag: "🇧🇷", label: "Português" },
                    { code: "es", flag: "🇪🇸", label: "Español" },
                  ].map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      aria-label={l.label}
                      title={l.label}
                      data-testid={`lang-flag-${l.code}`}
                      onClick={() => setLang(l.code)}
                      className={`h-10 w-12 rounded-lg text-xl leading-none flex items-center justify-center transition-colors ${
                        lang === l.code
                          ? "bg-primary/10 ring-2 ring-primary"
                          : "hover:bg-muted opacity-70 hover:opacity-100"
                      }`}
                    >
                      {l.flag}
                    </button>
                  ))}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="switch-profile-button" onClick={() => { logout(); navigate("/"); }}>
                  <LogOut className="h-4 w-4 mr-2" /> {t("sign_out")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Right column: top bar (static) + scrollable content + bottom nav (static) */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* Top spacer — EXACTLY the iOS status bar / Dynamic Island height
            (env safe-area inset). Applies on ALL sizes so the iPad (which uses
            the sidebar layout) also clears the clock/battery status bar.
            Collapses to 0 on browsers without a notch. */}
        <div
          className="shrink-0 z-40 bg-card"
          style={{ height: "env(safe-area-inset-top, 0px)" }}
          data-testid="mobile-top-bar"
        />

        {/* Voice note keeps playing after leaving the chat (WhatsApp-style bar) */}
        <VoiceMiniPlayer />
        {/* Background video uploads from OTHER chats keep going — show where they are */}
        <UploadProgressBar />

        {/* Company invite approval card (the person must accept to join) */}
        <CompanyInviteBanner />

        {(!online || queuedCount > 0 || syncInfo.syncing) && (() => {
          const photoN = queued.filter((i) => i.kind === "photo").length;
          const textN = queuedCount - photoN;
          const parts = [];
          if (photoN) parts.push(`${photoN} ${(photoN > 1 ? t("photos") : t("photo")).toLowerCase()}`);
          if (textN) parts.push(`${textN} ${(textN > 1 ? t("msgs") : t("type_message")).toLowerCase()}`);
          const detail = parts.length ? `${parts.join(" · ")} ${t("waiting_conn")}` : "";
          const offlineMode = user && offlineEnabled(user.id);
          const offlineLabel = offlineMode ? t("offline_banner_mode") : t("offline_banner");
          return (
            <button
              type="button"
              data-testid="offline-banner"
              onClick={() => queuedCount > 0 && setQueueOpen(true)}
              className={`shrink-0 w-full px-4 py-1.5 text-xs font-medium flex items-center gap-2 border-b text-left ${!online ? "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/25" : "bg-muted text-muted-foreground"} ${queuedCount > 0 ? "cursor-pointer" : "cursor-default"}`}
            >
              {online && syncInfo.syncing && queuedCount === 0 ? <RefreshCw className="h-3.5 w-3.5 shrink-0 animate-spin" /> : <WifiOff className="h-3.5 w-3.5 shrink-0" />}
              <span className="truncate" data-testid="offline-banner-label">
                {!online
                  ? (detail ? `${offlineLabel} — ${detail}` : offlineLabel)
                  : queuedCount > 0
                    ? `${detail || `${queuedCount} ${t("in_queue")}`} — ${t("msg_sending")}`
                    : `${t("offline_syncing")} ${syncInfo.progress}%`}
              </span>
              {queuedCount > 0 && <ChevronRight className="h-3.5 w-3.5 ml-auto shrink-0" />}
            </button>
          );
        })()}

        {/* Offline send-queue panel: everything waiting for connection, with thumbnails */}
        <Dialog open={queueOpen} onOpenChange={(v) => { if (!v) setQueueOpen(false); }}>
          <DialogContent className="max-w-md" data-testid="offline-queue-panel">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2">
                <CloudOff className="h-5 w-5 text-primary" /> {t("offline_queue_title")}
                <Badge variant="secondary" className="text-[10px]">{queuedCount}</Badge>
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-[45vh] overflow-y-auto space-y-1.5">
              {queued.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">{t("queue_empty")}</p>
              )}
              {queued.map((qi) => {
                const thumb = qi.kind === "photo" ? (qi.payload?.editedDataUrl || qi.payload?.originalDataUrl) : null;
                const failed = qi.status === "failed";
                const sending = qi.status === "sending";
                return (
                  <div key={qi.client_id} data-testid={`queue-item-${qi.client_id}`} className="flex items-center gap-3 rounded-lg border px-2.5 py-2">
                    {thumb ? (
                      <img src={thumb} alt="" className="h-10 w-10 rounded object-cover shrink-0" />
                    ) : (
                      <div className="h-10 w-10 rounded bg-muted flex items-center justify-center shrink-0">
                        <MessageSquare className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium truncate">
                        {qi.kind === "photo" ? t("photo") : t("type_message")}{qi.preview ? ` · ${qi.preview}` : ""}
                      </div>
                      <div className={`text-[10px] flex items-center gap-1 mt-0.5 ${failed ? "text-destructive" : "text-muted-foreground"}`}>
                        {failed ? <CloudOff className="h-3 w-3" /> : <Clock className={`h-3 w-3 ${sending ? "animate-spin" : ""}`} />}
                        {failed ? t("msg_failed") : sending ? t("msg_sending") : t("msg_pending")}
                      </div>
                    </div>
                    <button
                      data-testid={`queue-item-discard-${qi.client_id}`}
                      onClick={() => removeQueued(qi.client_id)}
                      className="h-8 w-8 flex items-center justify-center rounded-lg border border-destructive/30 text-destructive hover:bg-destructive/10 shrink-0"
                      title={t("discard")}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                    {failed && (
                      <button
                        data-testid={`queue-item-retry-${qi.client_id}`}
                        onClick={() => retryQueued(qi.client_id)}
                        className="h-8 w-8 flex items-center justify-center rounded-lg border text-primary hover:bg-primary/10 shrink-0"
                        title={t("retry")}
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {queued.length > 0 && (
              <Button data-testid="queue-retry-all" onClick={() => flushQueue()} className="w-full h-10 rounded-xl" disabled={!online}>
                <RefreshCw className="h-4 w-4 mr-2" /> {online ? t("retry_now") : t("offline_banner")}
              </Button>
            )}
          </DialogContent>
        </Dialog>

        {/* Main content — the ONLY scrolling region */}
        <main className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col">
          {children}
        </main>

        {/* Mobile bottom tabs (hidden while the keyboard is open) */}
        <nav
          className={`md:hidden shrink-0 z-40 border-t bg-card ${kbOpen ? "hidden" : "flex"}`}
          style={{ paddingBottom: appOutdated ? "6px" : "max(calc(env(safe-area-inset-bottom, 0px) + 6px), 12px)" }}
        >
          {navItems.map((item) => (
            <button
              key={item.key}
              data-testid={`mobile-nav-${item.key}`}
              onClick={() => navigate(item.path)}
              className={`flex-1 flex flex-col items-center justify-center min-h-[56px] py-2 relative ${
                isActive(item.path) ? "text-primary" : "text-muted-foreground"
              }`}
            >
              <item.icon className="h-5 w-5" strokeWidth={2} />
              <span className="text-[10px] mt-0.5 font-medium">{item.label}</span>
              {item.badge > 0 && (
                <span className="absolute top-1.5 right-1/2 translate-x-4 bg-primary text-primary-foreground text-[9px] rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                  {item.badge > 99 ? "99+" : item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>
        {/* Yellow "update the app" bar — under the menus, native only, while outdated */}
        {!kbOpen && <UpdateBanner />}
      </div>

      {/* First-login welcome tour (replayable from More → App tour) */}
      <WelcomeTour />

      {/* Offers to save a FaceID/passkey when this device doesn't have one yet */}
      <PasskeyPrompt />

      {/* Floating ⚠️ chat-head — visible over every screen while there are open issues */}
      <FloatingIssueAlert />

      {/* Buddy — the animated AI field helper (voice assistant) */}
      {/* Buddy needs explicit AI consent (App Store 5.1.1/5.1.2) */}
      {user?.ai_consent === true && buddyEnabled(user) && <BuddyAssistant />}
      <BroadcastViewer />
      {user && <OfflineSetupDialog open={offlineAsk} firstRun user={user} t={t} onClose={() => setOfflineAsk(false)} />}
      {user && <OfflineFullDialog open={offlineFull} user={user} t={t} onClose={() => setOfflineFull(false)} />}
      <AIConsentDialog />
    </div>
  );
};
